import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  GraduationCap, Briefcase, CheckCircle2, ExternalLink, Save, Compass, Info,
} from 'lucide-react';

const PortalPathway = ({ onSaved }) => {
  const [catalog, setCatalog] = useState(null);
  const [pathway, setPathway] = useState(null);
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [pathwayType, setPathwayType] = useState('');
  const [licenseCode, setLicenseCode] = useState('');
  const [state, setState] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [startDate, setStartDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, pw] = await Promise.all([
        axios.get(`${API_URL}/internship-portal/requirements/catalog`),
        axios.get(`${API_URL}/internship-portal/pathway`),
      ]);
      setCatalog(cat.data || {});
      const p = pw.data.pathway;
      setPathway(p);
      setRequirement(pw.data.requirement);
      if (p) {
        setPathwayType(p.pathway_type || '');
        setLicenseCode(p.target_license_code || '');
        setState(p.state || '');
        setSchoolName(p.school_name || '');
        setStartDate(p.start_date || '');
      }
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live preview of matched requirement as the user picks options
  useEffect(() => {
    const fetchReq = async () => {
      if (!pathwayType || !licenseCode) { setRequirement(null); return; }
      if (pathwayType === 'postgrad' && !state) { setRequirement(null); return; }
      try {
        const res = await axios.get(`${API_URL}/internship-portal/requirements`, {
          params: { pathway: pathwayType, license_code: licenseCode, state: pathwayType === 'postgrad' ? state : undefined },
        });
        setRequirement(res.data.requirement);
      } catch (e) { setRequirement(null); }
    };
    fetchReq();
  }, [pathwayType, licenseCode, state]);

  const onChangePathwayType = (v) => { setPathwayType(v); setLicenseCode(''); setState(''); };
  const onChangeState = (v) => { setState(v); setLicenseCode(''); };

  const licenseOptions = () => {
    if (!catalog) return [];
    if (pathwayType === 'academic') return catalog.academic_options || [];
    if (pathwayType === 'postgrad' && state) return (catalog.postgrad_by_state || {})[state] || [];
    return [];
  };

  const save = async () => {
    if (!pathwayType) { toast.error('Choose your pathway type.'); return; }
    if (!licenseCode) { toast.error(pathwayType === 'academic' ? 'Choose your degree.' : 'Choose your target license.'); return; }
    if (pathwayType === 'postgrad' && !state) { toast.error('Choose your state.'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/internship-portal/pathway`, {
        pathway_type: pathwayType,
        target_license_code: licenseCode,
        state: pathwayType === 'postgrad' ? state : null,
        school_name: schoolName || null,
        start_date: startDate || null,
      });
      setPathway(res.data.pathway);
      setRequirement(res.data.requirement);
      toast.success('Pathway saved. Your hour requirements are set.');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your pathway.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-gray-500" data-testid="pathway-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="portal-pathway">
      <div className="bg-white rounded-2xl border p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Compass className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-serif text-xl font-bold text-primary">
              {pathway ? 'My Supervised-Experience Pathway' : 'Complete Your Pathway'}
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Tell us whether you're an MSW/BSW student in a field placement or a graduate completing supervised
              experience toward licensure. We'll set your official hour requirements automatically.
            </p>
          </div>
        </div>

        {/* Pathway type choice */}
        <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">Your Pathway</Label>
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <PathwayCard
            active={pathwayType === 'academic'} onClick={() => onChangePathwayType('academic')}
            icon={GraduationCap} title="Academic Field Placement"
            desc="I'm an MSW or BSW student completing a school field placement / practicum."
            testid="pathway-type-academic"
          />
          <PathwayCard
            active={pathwayType === 'postgrad'} onClick={() => onChangePathwayType('postgrad')}
            icon={Briefcase} title="Post-Graduate Supervised Experience"
            desc="I've graduated and I'm completing supervised hours toward a clinical/advanced license."
            testid="pathway-type-postgrad"
          />
        </div>

        {pathwayType && (
          <div className="grid sm:grid-cols-2 gap-4">
            {pathwayType === 'postgrad' && (
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">State</Label>
                <Select value={state || undefined} onValueChange={onChangeState}>
                  <SelectTrigger data-testid="pathway-state"><SelectValue placeholder="Choose your state" /></SelectTrigger>
                  <SelectContent>
                    {(catalog?.postgrad_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">{pathwayType === 'academic' ? 'Degree Program' : 'Target License'}</Label>
              <Select value={licenseCode || undefined} onValueChange={setLicenseCode} disabled={pathwayType === 'postgrad' && !state}>
                <SelectTrigger data-testid="pathway-license"><SelectValue placeholder={pathwayType === 'postgrad' && !state ? 'Choose a state first' : 'Choose'} /></SelectTrigger>
                <SelectContent>
                  {licenseOptions().map((o) => <SelectItem key={o.license_code} value={o.license_code}>{o.license_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">{pathwayType === 'academic' ? 'School / Program Name (optional)' : 'Agency / Employer (optional)'}</Label>
              <Input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder={pathwayType === 'academic' ? 'e.g., University of Texas at Arlington' : 'e.g., County Behavioral Health'} data-testid="pathway-school" />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Start Date (optional)</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="pathway-start" />
            </div>
          </div>
        )}

        {/* Matched requirement preview */}
        {pathwayType && licenseCode && (pathwayType === 'academic' || state) && (
          requirement ? (
            <div className="mt-5 bg-primary/5 border border-primary/15 rounded-xl p-5" data-testid="pathway-requirement">
              <p className="text-primary font-semibold flex items-center gap-2 mb-2"><CheckCircle2 className="w-5 h-5 text-secondary" /> Official Requirement Matched</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <ReqStat label="Total Hours" value={requirement.total_hours} />
                <ReqStat label="Supervision Hours" value={requirement.supervision_hours ?? '—'} />
                <ReqStat label="Direct Client Hours" value={requirement.direct_client_hours ?? '—'} />
                <ReqStat label="Minimum Months" value={requirement.min_months ?? '—'} />
              </div>
              <p className="text-sm text-gray-600">{requirement.summary}</p>
              {requirement.supervisor_requirement && (
                <p className="text-xs text-gray-500 mt-2"><strong>Supervisor:</strong> {requirement.supervisor_requirement}</p>
              )}
              {requirement.official_source_url && (
                <a href={requirement.official_source_url} target="_blank" rel="noopener noreferrer" className="text-secondary text-sm font-medium inline-flex items-center gap-1 mt-2">
                  {requirement.official_source_name || 'Official source'} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          ) : (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2" data-testid="pathway-no-requirement">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                We don't yet have the official board requirements loaded for this selection. You can still save your
                pathway and track hours — just enter your required hours manually under <strong>Hours &amp; Logs</strong>.
                We're adding more states and licenses regularly.
              </p>
            </div>
          )
        )}

        <div className="mt-6">
          <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="save-pathway-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : (pathway ? 'Update Pathway' : 'Save Pathway')}
          </Button>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">
          Requirements vary by state and may change. Always confirm the current rules with your licensing board or
          school field office. These figures are provided for planning only and are not legal advice.
        </p>
      </div>
    </div>
  );
};

const PathwayCard = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button
    onClick={onClick}
    className={`text-left rounded-xl border p-4 transition-all ${active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-gray-200 hover:border-secondary/40'}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-5 h-5 ${active ? 'text-primary' : 'text-gray-400'}`} />
      <span className="font-semibold text-primary">{title}</span>
    </div>
    <p className="text-sm text-gray-500">{desc}</p>
  </button>
);

const ReqStat = ({ label, value }) => (
  <div className="bg-white rounded-lg border p-3 text-center">
    <p className="text-xl font-bold text-primary">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

export default PortalPathway;
